const m = require('mithril');
const prop = require('mithril/stream');
const dragscroll = require('../../libs/dragscroll');
const loaders = require('../../layout/components/loaders');
const ToggleSwitch = require('../../components/toggleSwitch');

var componentDatasetVisibilityTable = {};

componentDatasetVisibilityTable.oninit = function () {
  this.search = prop('');
  this.expandedClasses = prop({});
  this.savingByRid = prop({});
  this.error = prop('');
};

componentDatasetVisibilityTable.view = function (vnode) {
  var state = vnode.state;
  var attrs = vnode.attrs || {};
  var rows = getDatasetVisibilityRows(attrs.parentRecord, {
    edgeName: attrs.edgeName,
    diagramRid: attrs.diagramRid,
    visibilityField: attrs.visibilityField
  });
  var query = (state.search() || '').toString().toLowerCase().trim();
  var filteredRows = filterDatasetVisibilityRows(rows, query);
  var groupedRows = groupDatasetVisibilityRowsByClass(filteredRows);
  var maxDatasetColumnCount = getMaxDatasetVisibilityColumnCount(groupedRows);
  var searchActive = Boolean(query);
  var title = attrs.title || 'Dataset Visibility';
  var emptyMessage = rows.length ? 'No equipment matches the search.' : (attrs.emptyMessage || 'No equipment datasets.');

  return m('div', { class: attrs.containerClass || 'mh3 mv3' }, [
    m('div.flex.flex-wrap.items-center.justify-between.bb.bw1.pb2.mb2', [
      m('div.f2', title),
      rows.length > 6 ?
        m('span.flex.items-center.ba.b--blue.br2.ph1.h2.mv1', [
          m('i.ion-search.blue.f4.mr1'),
          m('input.bn.bg-transparent.mh1.mv1.w4', {
            placeholder: 'Search',
            value: state.search(),
            oninput: function (ev) {
              state.search(ev.target.value);
            },
            style: {
              outline: 'none'
            }
          }),
          state.search() ?
            m('i.ion-close-circled.blue.f4.pointer.mr1', {
              onclick: function () {
                state.search('');
              }
            })
          :
            ''
        ])
      :
        ''
    ]),
    state.error() ? m('div.red.f5.mb2', state.error()) : '',
    filteredRows.length ?
      m('div.mb3', {
        style: {
          maxWidth: '100%',
          overflow: 'hidden'
        }
      }, [
        m('div.dragscroll.pb3', {
          oncreate: dragscroll,
          onupdate: dragscroll,
          style: {
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'hidden'
          }
        }, [
          m('table.w-100.f4.narrow', {
            style: {
              width: '100%',
              minWidth: 'max-content'
            }
          }, buildDatasetVisibilityTableSections(groupedRows, state, attrs, {
            expandAll: searchActive,
            maxDatasetColumnCount: maxDatasetColumnCount
          }))
        ])
      ])
    :
      m('div.moon-gray.f4.pv3', emptyMessage)
  ]);
};

function getDatasetVisibilityRows(parentRecord, options) {
  options = options || {};
  var edgeName = options.edgeName || getDefaultEdgeName(parentRecord);
  var diagramRid = normalizeRid(options.diagramRid || (parentRecord && parentRecord['@rid']));
  var visibilityField = options.visibilityField || 'visible';
  var componentEdges = ((parentRecord && parentRecord[edgeName]) || [])
    .slice()
    .filter(function (edge) {
      return edge && edge.in && !edge.in.deleted;
    });

  return componentEdges.map(function (edge, index) {
    var childEquipmentRecord = edge.in;
    var name = getRecordLabel(childEquipmentRecord);
    var className = childEquipmentRecord['@class'] || '';
    var datasets = getComponentDatasets(parentRecord, childEquipmentRecord);
    var datasetCells = getDatasetVisibilityCells(datasets, visibilityField, diagramRid);
    var datasetCellsByKey = {};
    datasetCells.forEach(function (cell) {
      datasetCellsByKey[cell.key] = cell;
    });

    return {
      key: edge['@rid'] || childEquipmentRecord['@rid'] || ['equipment', className, name, index].join('-'),
      childEquipmentRecord: childEquipmentRecord,
      name: name,
      className: className,
      href: buildEquipmentHref(childEquipmentRecord),
      datasets: datasets,
      datasetCells: datasetCells,
      datasetCellsByKey: datasetCellsByKey,
      searchText: [
        name,
        className,
        datasetCells.map(formatDatasetVisibilityCellSearchText).join(' ')
      ].join(' ').toLowerCase()
    };
  });
}

function getDefaultEdgeName(parentRecord) {
  return parentRecord && parentRecord['@class'] === 'Diagram'
    ? 'out_hasDiagramEquipment'
    : 'out_hasComponent';
}

function getRecordLabel(record) {
  return (record && (record.name || record['@class'])) || '';
}

function buildEquipmentHref(record) {
  if (!record || !record['@rid']) return '#';
  return '/#!/equipment/' + (record['@class'] || 'equipment').toLowerCase() + '/' + record['@rid'].slice(1);
}

function filterDatasetVisibilityRows(rows, query) {
  var normalizedQuery = (query || '').toString().toLowerCase().trim();
  if (!normalizedQuery) return rows;
  return rows.filter(function (row) {
    return row.searchText.indexOf(normalizedQuery) !== -1;
  });
}

function groupDatasetVisibilityRowsByClass(rows) {
  var groupsByClass = {};
  rows.forEach(function (row) {
    var className = row.className || 'Equipment';
    if (!groupsByClass[className]) {
      groupsByClass[className] = {
        className: className,
        rows: []
      };
    }
    groupsByClass[className].rows.push(row);
  });

  return Object.keys(groupsByClass)
    .sort(compareLabels)
    .map(function (className) {
      var group = groupsByClass[className];
      group.rows.sort(function (a, b) {
        return compareLabels(a.name || a.className, b.name || b.className);
      });
      group.datasetColumns = getDatasetVisibilityColumns(group.rows);
      return group;
    });
}

function buildDatasetVisibilityTableSections(groupedRows, state, attrs, options) {
  options = options || {};
  var maxDatasetColumnCount = options.maxDatasetColumnCount !== undefined
    ? options.maxDatasetColumnCount
    : getMaxDatasetVisibilityColumnCount(groupedRows);
  var sections = [];
  groupedRows.forEach(function (group) {
    var datasetColumns = group.datasetColumns || [];
    var expanded = options.expandAll || isDatasetVisibilityClassExpanded(state, group.className);
    sections.push(m('tbody', { key: 'class-section-' + group.className }, [
      renderDatasetVisibilityClassGroupRow(group, state, expanded, datasetColumns, maxDatasetColumnCount)
    ]));
    if (expanded) {
      sections.push(m('tbody', { key: 'equipment-section-' + group.className }, [
        renderDatasetVisibilityGroupHeaderRow(group, datasetColumns, maxDatasetColumnCount),
        renderDatasetVisibilityBulkRow(group, state, attrs, datasetColumns, maxDatasetColumnCount)
      ].concat(group.rows.map(function (row) {
        return renderDatasetVisibilityTableRow(row, state, attrs, datasetColumns, maxDatasetColumnCount);
      }))));
    }
  });
  return sections;
}

function isDatasetVisibilityClassExpanded(state, className) {
  var expandedClasses = state.expandedClasses ? state.expandedClasses() : {};
  return Boolean(expandedClasses && expandedClasses[className]);
}

function toggleDatasetVisibilityClass(state, className) {
  var expandedClasses = Object.assign({}, state.expandedClasses ? state.expandedClasses() : {});
  if (expandedClasses[className]) {
    delete expandedClasses[className];
  } else {
    expandedClasses[className] = true;
  }
  state.expandedClasses(expandedClasses);
}

function renderDatasetVisibilityClassGroupRow(group, state, expanded, datasetColumns, maxDatasetColumnCount) {
  return m('tr', { key: 'class-' + group.className }, [
    m('td.bb.bl-0.ph2.pv1.light-silver.f5', {
      colspan: getDatasetVisibilityGroupColspan(group, datasetColumns, maxDatasetColumnCount),
      role: 'button',
      tabindex: 0,
      'aria-expanded': expanded,
      onclick: function () {
        toggleDatasetVisibilityClass(state, group.className);
      },
      onkeydown: function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          toggleDatasetVisibilityClass(state, group.className);
        }
      },
      style: {
        backgroundColor: '#3a3a3a',
        cursor: 'pointer',
        lineHeight: '1.1'
      }
    }, [
      m('i.mr2', { class: expanded ? 'ion-ios-arrow-down' : 'ion-ios-arrow-forward' }),
      group.className,
      m('span.light-silver.ml2', '(' + group.rows.length + ')')
    ])
  ]);
}

function renderDatasetVisibilityGroupHeaderRow(group, datasetColumns, maxDatasetColumnCount) {
  datasetColumns = datasetColumns || group.datasetColumns || [];
  return m('tr', { key: 'header-' + group.className }, [
    m('th.bb.bl-0.bw1.tl.pl4.pr2.pv1.fw1.f5', 'Name')
  ].concat(datasetColumns.map(function (datasetColumn) {
    return m('th.bb.bl-0.bw1.tc.ph2.pv1.fw1.f5', [
      m('div.nowrap', datasetColumn.datasetName)
    ]);
  })).concat(renderDatasetVisibilityPaddingHeader(datasetColumns, maxDatasetColumnCount)));
}

function renderDatasetVisibilityPaddingHeader(datasetColumns, maxDatasetColumnCount) {
  var colspan = getDatasetVisibilityPaddingColspan(datasetColumns, maxDatasetColumnCount);
  return colspan > 0
    ? [m('th.bb.bl-0.bw1.tc.ph2.pv1.fw1.f5', { colspan: colspan }, '')]
    : [];
}

function renderDatasetVisibilityTableRow(row, state, attrs, datasetColumns, maxDatasetColumnCount) {
  datasetColumns = datasetColumns || [];
  return m('tr', { key: row.key }, [
    m('td.bb.bl-0.pl4.pr2.pv1.v-mid.f5', [
      m('a.blue.pointer.truncate.db', {
        href: row.href,
        title: row.name
      }, row.name)
    ])
  ].concat(datasetColumns.map(function (datasetColumn) {
    return renderDatasetVisibilityCell(row, state, attrs, datasetColumn);
  })).concat(renderDatasetVisibilityPaddingCell(datasetColumns, maxDatasetColumnCount)));
}

function renderDatasetVisibilityBulkRow(group, state, attrs, datasetColumns, maxDatasetColumnCount) {
  datasetColumns = datasetColumns || [];
  return m('tr', { key: 'bulk-' + group.className }, [
    m('td.bb.bl-0.pl4.pr2.pv1.v-mid.f5.moon-gray', 'All')
  ].concat(datasetColumns.map(function (datasetColumn) {
    return renderDatasetVisibilityBulkCell(group, state, attrs, datasetColumn);
  })).concat(renderDatasetVisibilityPaddingCell(datasetColumns, maxDatasetColumnCount)));
}

function renderDatasetVisibilityBulkCell(group, state, attrs, datasetColumn) {
  var cells = getDatasetVisibilityCellsForColumn(group.rows, datasetColumn);
  if (!cells.length) {
    return m('td.bb.bl-0.tc.moon-gray.f5', '-');
  }

  var savingByRid = state.savingByRid ? state.savingByRid() : {};
  var saving = cells.some(function (cell) {
    var datasetRid = normalizeRid(cell.dataset && cell.dataset['@rid']);
    return Boolean(datasetRid && savingByRid[datasetRid]);
  });
  var allVisible = cells.every(function (cell) {
    return isDatasetVisible(cell.dataset, {
      diagramRid: cell.diagramRid,
      visibilityField: cell.visibilityField
    });
  });
  var ariaLabel = [
    allVisible ? 'Hide' : 'Show',
    'all',
    datasetColumn.datasetName,
    'datasets for',
    group.className
  ].join(' ');

  return m('td.bb.bl-0.tc.ph2.pv1.v-mid', {
    title: allVisible ? 'Visible for all' : 'Not visible for all'
  }, [
    m(ToggleSwitch, {
      id: 'dataset-visibility-all-' + sanitizeForId(group.className + '-' + datasetColumn.key),
      checked: allVisible,
      disabled: saving,
      ariaLabel: ariaLabel,
      width: '2.6rem',
      height: '1.35rem',
      onchange: function (ev) {
        return updateDatasetVisibilityForColumn(group.rows, datasetColumn, ev.target.checked, state, attrs);
      }
    })
  ]);
}

function renderDatasetVisibilityPaddingCell(datasetColumns, maxDatasetColumnCount) {
  var colspan = getDatasetVisibilityPaddingColspan(datasetColumns, maxDatasetColumnCount);
  return colspan > 0
    ? [m('td.bb.bl-0.ph2.pv1', { colspan: colspan }, '')]
    : [];
}

function getDatasetVisibilityGroupColspan(group, datasetColumns, maxDatasetColumnCount) {
  var columns = datasetColumns || (group && group.datasetColumns) || [];
  return 1 + Math.max(maxDatasetColumnCount || 0, columns.length);
}

function getDatasetVisibilityPaddingColspan(datasetColumns, maxDatasetColumnCount) {
  var columnCount = (datasetColumns || []).length;
  return Math.max((maxDatasetColumnCount || 0) - columnCount, 0);
}

function getComponentDatasets(parentRecord, childEquipmentRecord) {
  var containsEdges = (childEquipmentRecord.out_Contains || [])
    .slice()
    .filter(function (containsEdge) {
      var record = containsEdge && containsEdge.in;
      return record && (
        (record['@class'] === 'Dataset' ||
          Array.isArray(record.columns) ||
          Array.isArray(record.out_Column)) &&
        isDiagramDatasetType(record)
      );
    })
    .sort(sortDatasetContainsEdges);

  return containsEdges.map(function (containsEdge) {
    var datasetRecord = Object.assign({}, containsEdge.in);
    datasetRecord.path = buildComponentDatasetPath(parentRecord, childEquipmentRecord, datasetRecord);
    return datasetRecord;
  });
}

function sortDatasetContainsEdges(a, b) {
  var leftDataset = a && a.in;
  var rightDataset = b && b.in;
  var leftDefault = getDatasetLabel(leftDataset) === 'Default';
  var rightDefault = getDatasetLabel(rightDataset) === 'Default';
  if (leftDefault !== rightDefault) return leftDefault ? -1 : 1;

  var leftIndex = Number(a && a.index);
  var rightIndex = Number(b && b.index);
  if (Number.isFinite(leftIndex) && Number.isFinite(rightIndex) && leftIndex !== rightIndex) {
    return leftIndex - rightIndex;
  }
  if (Number.isFinite(leftIndex)) return -1;
  if (Number.isFinite(rightIndex)) return 1;

  return compareLabels(getDatasetLabel(leftDataset), getDatasetLabel(rightDataset));
}

function buildComponentDatasetPath(parentRecord, childEquipmentRecord, datasetRecord) {
  var parentPath = ((parentRecord && parentRecord.path) || []).map(toPathRecord);
  return parentPath.concat([
    toPathRecord(childEquipmentRecord),
    toPathRecord(datasetRecord)
  ]);
}

function toPathRecord(record) {
  return {
    '@rid': record && record['@rid'],
    name: record && (record.name || record['@class']),
    '@class': record && record['@class']
  };
}

function getDatasetVisibilityCells(datasets, visibilityField, diagramRid) {
  return (datasets || []).map(function (dataset, index) {
    var datasetName = getDatasetLabel(dataset);
    return {
      key: getDatasetVisibilityColumnKey(dataset, index),
      dataset: dataset,
      datasetName: datasetName,
      visible: isDatasetVisible(dataset, {
        diagramRid: diagramRid,
        visibilityField: visibilityField
      }),
      diagramRid: diagramRid,
      visibilityField: visibilityField
    };
  });
}

function getDatasetVisibilityColumns(rows) {
  var columnsByKey = {};
  var columns = [];
  rows.forEach(function (row) {
    (row.datasetCells || []).forEach(function (cell) {
      if (!columnsByKey[cell.key]) {
        columnsByKey[cell.key] = true;
        columns.push({
          key: cell.key,
          datasetName: cell.datasetName,
          visibilityField: cell.visibilityField
        });
      }
    });
  });
  return columns;
}

function getMaxDatasetVisibilityColumnCount(groupedRows) {
  return (groupedRows || []).reduce(function (maxCount, group) {
    return Math.max(maxCount, ((group && group.datasetColumns) || []).length);
  }, 0);
}

function renderDatasetVisibilityCell(row, state, attrs, datasetColumn) {
  var cell = getDatasetVisibilityCellForColumn(row, datasetColumn);
  if (!cell) {
    return m('td.bb.bl-0.tc.moon-gray.f5', '-');
  }

  var datasetRid = normalizeRid(cell.dataset && cell.dataset['@rid']);
  var savingByRid = state.savingByRid ? state.savingByRid() : {};
  var saving = Boolean(datasetRid && savingByRid[datasetRid]);
  var visible = isDatasetVisible(cell.dataset, {
    diagramRid: cell.diagramRid,
    visibilityField: cell.visibilityField
  });
  var ariaLabel = [
    visible ? 'Hide' : 'Show',
    cell.datasetName,
    'for',
    row.name
  ].join(' ');

  return m('td.bb.bl-0.tc.ph2.pv1.v-mid', {
    title: visible ? 'Visible' : 'Hidden'
  }, [
    m(ToggleSwitch, {
      id: 'dataset-visibility-' + sanitizeForId(row.key + '-' + cell.key),
      checked: visible,
      disabled: saving,
      ariaLabel: ariaLabel,
      width: '2.6rem',
      height: '1.35rem',
      onchange: function (ev) {
        return updateDatasetVisibility(cell.dataset, cell.visibilityField, ev.target.checked, state, attrs);
      }
    })
  ]);
}

function getDatasetVisibilityCellForColumn(row, datasetColumn) {
  if (!row || !datasetColumn) return null;
  return row.datasetCellsByKey && row.datasetCellsByKey[datasetColumn.key];
}

function getDatasetVisibilityCellsForColumn(rows, datasetColumn) {
  return (rows || []).map(function (row) {
    return getDatasetVisibilityCellForColumn(row, datasetColumn);
  }).filter(Boolean);
}

function updateDatasetVisibility(dataset, visibilityField, visible, state, attrs) {
  var datasetRid = normalizeRid(dataset && dataset['@rid']);
  var diagramRid = normalizeRid(attrs && (attrs.diagramRid || (attrs.parentRecord && attrs.parentRecord['@rid'])));
  if (!datasetRid) return Promise.resolve();
  if (!diagramRid) {
    state.error('Unable to update dataset visibility without a diagram.');
    return Promise.resolve();
  }

  var update = buildDatasetVisibilityUpdate(dataset, diagramRid, visibilityField, visible);
  var savingByRid = Object.assign({}, state.savingByRid ? state.savingByRid() : {});

  savingByRid[datasetRid] = true;
  state.savingByRid(savingByRid);
  state.error('');
  dataset.diagramData = update.nextDiagramData;

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/diagram/' + diagramRid.slice(1) + '/movediagram',
    method: 'PUT',
    body: {
      nodesAndLinksMoved: [
        update.move
      ]
    }
  })
  .then(function (result) {
    if (attrs && typeof attrs.onSaved === 'function') {
      return Promise.resolve(attrs.onSaved({
        datasetRid: datasetRid,
        diagramRid: diagramRid,
        visible: visible,
        visibilityField: visibilityField,
        result: result
      }));
    }
  })
  .catch(function (error) {
    dataset.diagramData = update.previousDiagramData;
    state.error(error && (error.message || error.error) || 'Unable to update dataset visibility.');
  })
  .then(function () {
    var updatedSavingByRid = Object.assign({}, state.savingByRid ? state.savingByRid() : {});
    delete updatedSavingByRid[datasetRid];
    state.savingByRid(updatedSavingByRid);
    m.redraw();
  });
}

function updateDatasetVisibilityForColumn(rows, datasetColumn, visible, state, attrs) {
  var diagramRid = normalizeRid(attrs && (attrs.diagramRid || (attrs.parentRecord && attrs.parentRecord['@rid'])));
  if (!diagramRid) {
    state.error('Unable to update dataset visibility without a diagram.');
    return Promise.resolve();
  }

  var cells = getDatasetVisibilityCellsForColumn(rows, datasetColumn);
  var updates = cells.map(function (cell) {
    return buildDatasetVisibilityUpdate(cell.dataset, diagramRid, cell.visibilityField, visible);
  }).filter(function (update) {
    return update && update.datasetRid;
  });
  if (!updates.length) return Promise.resolve();

  var savingByRid = Object.assign({}, state.savingByRid ? state.savingByRid() : {});
  updates.forEach(function (update) {
    savingByRid[update.datasetRid] = true;
    update.dataset.diagramData = update.nextDiagramData;
  });
  state.savingByRid(savingByRid);
  state.error('');

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/diagram/' + diagramRid.slice(1) + '/movediagram',
    method: 'PUT',
    body: {
      nodesAndLinksMoved: updates.map(function (update) {
        return update.move;
      })
    }
  })
  .then(function (result) {
    if (attrs && typeof attrs.onSaved === 'function') {
      return Promise.resolve(attrs.onSaved({
        datasetRids: updates.map(function (update) { return update.datasetRid; }),
        diagramRid: diagramRid,
        visible: visible,
        visibilityField: (datasetColumn && datasetColumn.visibilityField) || 'visible',
        result: result
      }));
    }
  })
  .catch(function (error) {
    updates.forEach(function (update) {
      update.dataset.diagramData = update.previousDiagramData;
    });
    state.error(error && (error.message || error.error) || 'Unable to update dataset visibility.');
  })
  .then(function () {
    var updatedSavingByRid = Object.assign({}, state.savingByRid ? state.savingByRid() : {});
    updates.forEach(function (update) {
      delete updatedSavingByRid[update.datasetRid];
    });
    state.savingByRid(updatedSavingByRid);
    m.redraw();
  });
}

function buildDatasetVisibilityUpdate(dataset, diagramRid, visibilityField, visible) {
  var datasetRid = normalizeRid(dataset && dataset['@rid']);
  if (!datasetRid) return null;

  visibilityField = visibilityField || 'visible';
  var previousDiagramData = dataset.diagramData && typeof dataset.diagramData === 'object'
    ? Object.assign({}, dataset.diagramData)
    : {};
  var previousEntry = previousDiagramData[diagramRid] && typeof previousDiagramData[diagramRid] === 'object'
    ? Object.assign({}, previousDiagramData[diagramRid])
    : {};
  var nextDiagramData = Object.assign({}, previousDiagramData, {
    [diagramRid]: Object.assign({}, previousEntry, {
      [visibilityField]: visible === true
    })
  });

  return {
    dataset: dataset,
    datasetRid: datasetRid,
    previousDiagramData: previousDiagramData,
    nextDiagramData: nextDiagramData,
    move: {
      '@rid': datasetRid,
      category: 'Dataset',
      body: {
        diagramData: nextDiagramData
      }
    }
  };
}

function getDatasetVisibilityColumnKey(dataset, index) {
  return getDatasetLabel(dataset) || ('Dataset ' + index);
}

function getDatasetLabel(dataset) {
  return (dataset && (dataset.name || dataset['@class'])) || 'Dataset';
}

function isDatasetVisible(dataset, options) {
  options = typeof options === 'string' ? { visibilityField: options } : (options || {});
  var override = getDatasetDiagramVisibility(dataset, options.diagramRid, options.visibilityField || 'visible');
  if (override !== null) return override;
  return !isTruthy(dataset && dataset.hideInParentDiagram);
}

function getDatasetDiagramVisibility(dataset, diagramRid, visibilityField) {
  var normalizedDiagramRid = normalizeRid(diagramRid);
  var field = visibilityField || 'visible';
  if (!dataset || !normalizedDiagramRid || !dataset.diagramData || typeof dataset.diagramData !== 'object') {
    return null;
  }
  var entry = dataset.diagramData[normalizedDiagramRid];
  if (!entry || typeof entry !== 'object' || entry[field] === undefined || entry[field] === null) {
    return null;
  }
  return isVisibleValue(entry[field]);
}

function isVisibleValue(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    var normalized = value.toLowerCase().trim();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  }
  return Boolean(value);
}

function isTruthy(value) {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    var normalized = value.toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

function isDiagramDatasetType(dataset) {
  return dataset && (isDefaultDatasetRecord(dataset) || dataset.type === undefined || dataset.type === null || dataset.type === 'list');
}

function isDefaultDatasetRecord(dataset) {
  return !!(dataset && (dataset.name === 'Default' || dataset.isDefaultDataset === true || dataset.isDefaulDataset === true));
}

function normalizeRid(value) {
  var rid = String(value || '').trim();
  if (!rid) return '';
  return rid.charAt(0) === '#' ? rid : ('#' + rid);
}

function sanitizeForId(value) {
  return String(value || '').replace(/[^a-zA-Z0-9_-]+/g, '-');
}

function compareLabels(a, b) {
  return (a || '').toString().localeCompare((b || '').toString(), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
}

function formatDatasetVisibilityCellSearchText(cell) {
  return [
    cell.datasetName,
    cell.visible ? 'visible' : 'hidden'
  ].filter(Boolean).join(' ');
}

componentDatasetVisibilityTable._private = {
  getDatasetVisibilityRows,
  filterDatasetVisibilityRows,
  groupDatasetVisibilityRowsByClass,
  buildDatasetVisibilityTableSections,
  isDatasetVisibilityClassExpanded,
  toggleDatasetVisibilityClass,
  renderDatasetVisibilityTableRow,
  getDatasetVisibilityCells,
  getDatasetVisibilityColumns,
  getMaxDatasetVisibilityColumnCount,
  getDatasetVisibilityCellForColumn,
  getDatasetVisibilityCellsForColumn,
  updateDatasetVisibility,
  updateDatasetVisibilityForColumn,
  buildDatasetVisibilityUpdate,
  isDatasetVisible,
  getDatasetDiagramVisibility,
  getDefaultEdgeName
};

module.exports = componentDatasetVisibilityTable;
